import { useCallback, useEffect, useRef, useState } from 'react';

import type { TabsProps } from './Tabs.types';

import { S } from './Tabs.styles';

export const Tabs = ({ activeTab = '', onChange, tabs = ['Tab1', 'Tab2', 'Tab3'] }: TabsProps) => {
  const [scrollHandleStyle, setScrollHandleStyle] = useState({ left: 0, width: 0 });

  const scrollRef = useRef<HTMLDivElement>(null);

  const onTabClick = (tab: string, index: number) => {
    onChange(tab, String(index));
  };

  const updateScrollHandlePosition = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = scrollElement;

    if (scrollWidth <= clientWidth) {
      setScrollHandleStyle({ left: 0, width: 0 });
      return;
    }

    const handleWidth = 74;
    const trackWidth = clientWidth;
    const scrollPercentage = scrollLeft / (scrollWidth - clientWidth);
    const handleLeft = scrollPercentage * (trackWidth - handleWidth);

    setScrollHandleStyle({ left: handleLeft, width: handleWidth });
  }, []);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    updateScrollHandlePosition();

    scrollElement.addEventListener('scroll', updateScrollHandlePosition, { passive: true });

    return () => {
      scrollElement.removeEventListener('scroll', updateScrollHandlePosition);
    };
  }, [updateScrollHandlePosition]);

  const showCustomScrollHandle = scrollHandleStyle.width > 0 && scrollHandleStyle.width < 100;

  return (
    <S.Container>
      <S.TabList ref={scrollRef}>
        {tabs.map((tab, index) => (
          <S.Tab
            key={tab}
            $isActive={activeTab === tab}
            $isScrollable={showCustomScrollHandle}
            onClick={() => onTabClick(tab, index)}
          >
            {tab}
          </S.Tab>
        ))}
      </S.TabList>

      {showCustomScrollHandle && (
        <S.ScrollTrack>
          <S.ScrollHandle
            style={{
              left: `${scrollHandleStyle.left}px`,
              width: `${scrollHandleStyle.width}px`
            }}
          />
        </S.ScrollTrack>
      )}
    </S.Container>
  );
};
